import React, { useState, useRef, useEffect } from "react";
import { Search } from "@mui/icons-material";
import CalendorIcon from "../../../../src/assests/images/calendar-2.svg";
import sorterArrow from "../../../../src/assests/images/SortingArrow.svg";
import {
    Box,
    Link,
    Typography,
    TextField,
    InputAdornment,
    Button,
    ListItemText,
    ListItemButton,
    List,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { debounce } from "lodash";
import ReportOutletTable from "./ReportOutletTable";



const CustomInput = React.forwardRef<HTMLInputElement, any>(
    ({ value, onClick }, ref) => (
        <div
            onClick={onClick}
            style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #ccc",
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "0 10px",
                height: "48px",
                width: "301px",
                cursor: "pointer",
            }}
        >
            <input
                ref={ref}
                value={value || "1 Sep 2024 to 15 Dec 2024"}
                style={{
                    fontSize: "14px",
                    border: "none",
                    outline: "none",
                    flex: 1,
                    color: "#000",
                    backgroundColor: "transparent",
                }}
                readOnly
            />
            <img
                src={CalendorIcon}
                alt="Calendar Icon"
                style={{ height: "20px", width: "20px", cursor: "pointer" }}
            />
        </div>
    )
);

function ReportsOutlet() {
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        null,
        null,
    ]);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupPosition, setPopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const sortButtonRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [startDate, endDate] = dateRange;
    const [sortOption, setSortOption] = useState<"highest" | "lowest" | null>(
        null
    );

    const handleSortButtonClick = () => setIsPopupOpen(!isPopupOpen);

    // ✅ debounce search
    useEffect(() => {
        const handleDebounce = debounce(
            (query: string) => setDebouncedSearch(query),
            300
        );
        handleDebounce(searchQuery);
        return () => handleDebounce.cancel();
    }, [searchQuery]);

    // ✅ calculate popup position on open
    useEffect(() => {
        if (sortButtonRef.current && isPopupOpen) {
            const rect = sortButtonRef.current.getBoundingClientRect();
            setPopupPosition({
                top: rect.bottom + window.scrollY + 10,
                left: rect.right - 320, // aligns right side
            });
        }
    }, [isPopupOpen]);

    // ✅ close popup on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target as Node) &&
                !sortButtonRef.current?.contains(event.target as Node)
            ) {
                setIsPopupOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSortSelection = (option: "highest" | "lowest") => {
        setSortOption(option);
        setIsPopupOpen(false);
    };

    return (
        <Box sx={{ background: "#fff", paddingTop: "30px" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0 32px",
                }}
            >
                <Box>
                    <Link
                        href="#"
                        sx={{
                            textDecoration: "none",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#696A6B",
                            paddingRight: "10px",
                        }}
                    >
                        Master Outlet Reports
                    </Link>
                    <Typography
                        variant="h3"
                        sx={{
                            fontSize: "24px",
                            fontWeight: "600",
                            color: "#000",
                            marginTop: "16px",
                        }}
                    >
                        Master  Outlet Reports
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "16px",
                    padding: "0 32px 30px",
                }}
            >
                {/* Search */}
                <Box sx={{ maxWidth: "520px", flexGrow: 1 }}>
                    <TextField
                        value={searchQuery}
                        onChange={(e: { target: { value: React.SetStateAction<string>; }; }) => setSearchQuery(e.target.value)}
                        placeholder="Search by Task, User ID, Shop name, size & Location"
                        type="search"
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ height: "20px", width: "20px" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                "& fieldset": { border: "2px solid #F5F5F5" },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ display: "flex" }}>
                    {/* Date Picker */}
                    <Box sx={{ marginRight: "8px", width: "301px" }}>
                        <DatePicker
                            selectsRange
                            startDate={startDate || undefined}
                            endDate={endDate || undefined}
                            onChange={(update: [Date | null, Date | null] | null) =>
                                setDateRange(update ?? [null, null])
                            }
                            customInput={<CustomInput />}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Sort Popup */}
            {isPopupOpen && (
                <Box
                    ref={popupRef}
                    sx={{
                        position: "absolute",
                        top: `${popupPosition.top}px`,
                        left: `${popupPosition.left}px`,
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        padding: "10px 0",
                        zIndex: 999,
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        width: "320px",
                    }}
                >
                    <List>
                        {[
                            { label: "Highest to Lowest Points", value: "highest" },
                            { label: "Lowest to Highest Points", value: "lowest" },
                        ].map(({ label, value }) => (
                            <ListItemButton
                                key={value}
                                onClick={() => handleSortSelection(value as "highest" | "lowest")}
                            >
                                <ListItemText primary={label} />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            )}

            {/* Rewards Table placeholder */}
            <Box
                sx={{
                    width: "100%",
                    background: "#f9fbfc",
                    padding: "20px 30px",
                    borderTop: "1px solid #d3d3d3",
                }}
            >
                {/* <RewardsTable
          startDate={startDate}
          endDate={endDate}
          searchQuery={debouncedSearch}
          sortOption={sortOption}
        /> */}
                <ReportOutletTable />
            </Box>
        </Box>
    );
}

export default ReportsOutlet;
